export type EnvMap = Record<string, string | undefined>;
export type EnvPolicyMode = 'strict' | 'warn-default';
export type EnvWarnFn = (message: string) => void;

export type EnvParseOutcome<T> =
    | { status: 'value'; value: T }
    | { status: 'empty' }
    | { status: 'invalid'; raw: string };

const TRUE_TOKENS = new Set(['1', 'true', 'yes', 'on']);
const FALSE_TOKENS = new Set(['0', 'false', 'no', 'off']);

const normalizeToken = (raw: string): string => raw.trim().toLowerCase();

const getEnvRawValue = (env: EnvMap | undefined, envKey: string): string | undefined => {
    if (env) {
        return env[envKey];
    }

    return process.env[envKey];
};

const invalidOutcome = (raw: string | undefined): EnvParseOutcome<never> => ({
    status: 'invalid',
    raw: raw ?? '',
});

const resolveParsedEnvOutcome = <T>({
    envKey,
    outcome,
    policyMode = 'strict',
    onEmpty,
    onInvalid,
    warn,
    invalidWarning,
    strictErrorMessage,
}: {
    envKey: string;
    outcome: EnvParseOutcome<T>;
    policyMode?: EnvPolicyMode;
    onEmpty: () => T;
    onInvalid: (raw: string) => T;
    warn?: EnvWarnFn;
    invalidWarning?: (raw: string) => string;
    strictErrorMessage?: (raw: string) => string;
}): T => {
    if (outcome.status === 'value') {
        return outcome.value;
    }

    if (outcome.status === 'empty') {
        return onEmpty();
    }

    if (policyMode === 'strict') {
        throw new Error(
            strictErrorMessage ? strictErrorMessage(outcome.raw) : `[env] Invalid ${envKey}="${outcome.raw}"`,
        );
    }

    if (invalidWarning && warn) {
        warn(invalidWarning(outcome.raw));
    }

    return onInvalid(outcome.raw);
};

export const parseBooleanValue = (raw: string | undefined): EnvParseOutcome<boolean> => {
    if (raw === undefined) {
        return { status: 'empty' };
    }

    const normalized = normalizeToken(raw);
    if (!normalized) {
        return { status: 'empty' };
    }

    if (TRUE_TOKENS.has(normalized)) {
        return { status: 'value', value: true };
    }

    if (FALSE_TOKENS.has(normalized)) {
        return { status: 'value', value: false };
    }

    return invalidOutcome(raw);
};

export const parseTrueOnlyBooleanValue = (raw: string | undefined): EnvParseOutcome<boolean> => {
    if (raw === undefined) {
        return { status: 'empty' };
    }

    const normalized = normalizeToken(raw);
    if (!normalized) {
        return { status: 'empty' };
    }

    if (TRUE_TOKENS.has(normalized)) {
        return { status: 'value', value: true };
    }

    return invalidOutcome(raw);
};

export const parsePositiveIntegerValue = (raw: string | undefined): EnvParseOutcome<number> => {
    if (raw === undefined) {
        return { status: 'empty' };
    }

    const trimmed = raw.trim();
    if (!trimmed) {
        return { status: 'empty' };
    }

    const parsed = Number(trimmed);
    if (Number.isInteger(parsed) && parsed > 0) {
        return { status: 'value', value: parsed };
    }

    return invalidOutcome(raw);
};

export const parseNonNegativeIntegerValue = (raw: string | undefined): EnvParseOutcome<number> => {
    if (raw === undefined) {
        return { status: 'empty' };
    }

    const trimmed = raw.trim();
    if (!trimmed) {
        return { status: 'empty' };
    }

    const parsed = Number(trimmed);
    if (Number.isInteger(parsed) && parsed >= 0) {
        return { status: 'value', value: parsed };
    }

    return invalidOutcome(raw);
};

export const parsePositiveNumberValue = (raw: string | undefined): EnvParseOutcome<number> => {
    if (raw === undefined) {
        return { status: 'empty' };
    }

    const trimmed = raw.trim();
    if (!trimmed) {
        return { status: 'empty' };
    }

    const parsed = Number(trimmed);
    if (Number.isFinite(parsed) && parsed > 0) {
        return { status: 'value', value: parsed };
    }

    return invalidOutcome(raw);
};

export const parseUnitIntervalValue = (raw: string | undefined): EnvParseOutcome<number> => {
    if (raw === undefined) {
        return { status: 'empty' };
    }

    const trimmed = raw.trim();
    if (!trimmed) {
        return { status: 'empty' };
    }

    const parsed = Number(trimmed);
    if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 1) {
        return { status: 'value', value: parsed };
    }

    return invalidOutcome(raw);
};

export const parseOptionalUnlimitedNonNegativeIntegerValue = (raw: string | undefined): EnvParseOutcome<number> => {
    if (raw === undefined) {
        return { status: 'empty' };
    }

    const trimmed = raw.trim();
    if (!trimmed) {
        return { status: 'empty' };
    }

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return invalidOutcome(raw);
    }

    return { status: 'value', value: Math.floor(parsed) };
};

export const parseTokenizedEnumValue = <T extends string>(
    raw: string | undefined,
    allowedValues: readonly T[],
): EnvParseOutcome<T> => {
    if (raw === undefined) {
        return { status: 'empty' };
    }

    const normalized = normalizeToken(raw);
    if (!normalized) {
        return { status: 'empty' };
    }

    if (allowedValues.includes(normalized as T)) {
        return { status: 'value', value: normalized as T };
    }

    return invalidOutcome(raw);
};

export const parseRawTextValue = (raw: string | undefined): EnvParseOutcome<string> => {
    if (raw === undefined || raw.length === 0) {
        return { status: 'empty' };
    }

    return { status: 'value', value: raw };
};

export const parseTrimmedTextValue = (raw: string | undefined): EnvParseOutcome<string> => {
    if (raw === undefined) {
        return { status: 'empty' };
    }

    const trimmed = raw.trim();
    if (!trimmed) {
        return { status: 'empty' };
    }

    return { status: 'value', value: trimmed };
};

export const parseTokenizedTextValue = (raw: string | undefined): EnvParseOutcome<string> => {
    const parsed = parseTrimmedTextValue(raw);
    if (parsed.status !== 'value') {
        return parsed;
    }

    return { status: 'value', value: parsed.value.toLowerCase() };
};

type ResolveEnvWithDefaultOptions<T> = {
    envKey: string;
    defaultValue: T;
    env?: EnvMap;
    policyMode?: EnvPolicyMode;
    warn?: EnvWarnFn;
    invalidWarning?: (raw: string, defaultValue: T) => string;
};

export const resolveBooleanEnv = ({
    envKey,
    defaultValue,
    env,
    policyMode = 'strict',
    warn,
    invalidWarning,
}: ResolveEnvWithDefaultOptions<boolean>): boolean => {
    const raw = getEnvRawValue(env, envKey);
    return resolveParsedEnvOutcome({
        envKey,
        outcome: parseBooleanValue(raw),
        policyMode,
        onEmpty: () => defaultValue,
        onInvalid: () => defaultValue,
        warn,
        invalidWarning: invalidWarning ? (value) => invalidWarning(value, defaultValue) : undefined,
    });
};

export const resolveTrueOnlyBooleanEnv = ({
    envKey,
    defaultValue,
    env,
    policyMode = 'strict',
    warn,
    invalidWarning,
}: ResolveEnvWithDefaultOptions<boolean>): boolean => {
    const raw = getEnvRawValue(env, envKey);
    return resolveParsedEnvOutcome({
        envKey,
        outcome: parseTrueOnlyBooleanValue(raw),
        policyMode,
        onEmpty: () => defaultValue,
        onInvalid: () => defaultValue,
        warn,
        invalidWarning: invalidWarning ? (value) => invalidWarning(value, defaultValue) : undefined,
    });
};

export const resolvePositiveIntegerEnv = ({
    envKey,
    defaultValue,
    env,
    policyMode = 'strict',
    warn,
    invalidWarning,
}: ResolveEnvWithDefaultOptions<number>): number => {
    const raw = getEnvRawValue(env, envKey);
    return resolveParsedEnvOutcome({
        envKey,
        outcome: parsePositiveIntegerValue(raw),
        policyMode,
        onEmpty: () => defaultValue,
        onInvalid: () => defaultValue,
        warn,
        invalidWarning: invalidWarning ? (value) => invalidWarning(value, defaultValue) : undefined,
    });
};

export const resolveNonNegativeIntegerEnv = ({
    envKey,
    defaultValue,
    env,
    policyMode = 'strict',
    warn,
    invalidWarning,
}: ResolveEnvWithDefaultOptions<number>): number => {
    const raw = getEnvRawValue(env, envKey);
    return resolveParsedEnvOutcome({
        envKey,
        outcome: parseNonNegativeIntegerValue(raw),
        policyMode,
        onEmpty: () => defaultValue,
        onInvalid: () => defaultValue,
        warn,
        invalidWarning: invalidWarning ? (value) => invalidWarning(value, defaultValue) : undefined,
    });
};

export const resolvePositiveNumberEnv = ({
    envKey,
    defaultValue,
    env,
    policyMode = 'strict',
    warn,
    invalidWarning,
}: ResolveEnvWithDefaultOptions<number>): number => {
    const raw = getEnvRawValue(env, envKey);
    return resolveParsedEnvOutcome({
        envKey,
        outcome: parsePositiveNumberValue(raw),
        policyMode,
        onEmpty: () => defaultValue,
        onInvalid: () => defaultValue,
        warn,
        invalidWarning: invalidWarning ? (value) => invalidWarning(value, defaultValue) : undefined,
    });
};

export const resolveUnitIntervalEnv = ({
    envKey,
    defaultValue,
    env,
    policyMode = 'strict',
    warn,
    invalidWarning,
}: ResolveEnvWithDefaultOptions<number>): number => {
    const raw = getEnvRawValue(env, envKey);
    return resolveParsedEnvOutcome({
        envKey,
        outcome: parseUnitIntervalValue(raw),
        policyMode,
        onEmpty: () => defaultValue,
        onInvalid: () => defaultValue,
        warn,
        invalidWarning: invalidWarning ? (value) => invalidWarning(value, defaultValue) : undefined,
    });
};

export const resolveOptionalUnlimitedNonNegativeIntegerEnv = ({
    envKey,
    env,
    policyMode = 'strict',
    warn,
    invalidWarning,
}: {
    envKey: string;
    env?: EnvMap;
    policyMode?: EnvPolicyMode;
    warn?: EnvWarnFn;
    invalidWarning?: (raw: string) => string;
}): number | null => {
    const raw = getEnvRawValue(env, envKey);
    const outcome = parseOptionalUnlimitedNonNegativeIntegerValue(raw);

    const resolved = resolveParsedEnvOutcome({
        envKey,
        outcome,
        policyMode,
        onEmpty: () => null,
        onInvalid: () => null,
        warn,
        invalidWarning,
    });

    if (typeof resolved !== 'number') {
        return null;
    }

    return resolved > 0 ? resolved : null;
};

export const resolveTokenizedEnumEnv = <T extends string>({
    envKey,
    allowedValues,
    defaultValue,
    env,
    policyMode = 'strict',
    warn,
    invalidWarning,
}: {
    envKey: string;
    allowedValues: readonly T[];
    defaultValue: T;
    env?: EnvMap;
    policyMode?: EnvPolicyMode;
    warn?: EnvWarnFn;
    invalidWarning?: (raw: string, defaultValue: T) => string;
}): T => {
    const raw = getEnvRawValue(env, envKey);
    return resolveParsedEnvOutcome({
        envKey,
        outcome: parseTokenizedEnumValue(raw, allowedValues),
        policyMode,
        onEmpty: () => defaultValue,
        onInvalid: () => defaultValue,
        warn,
        invalidWarning: invalidWarning ? (value) => invalidWarning(value, defaultValue) : undefined,
    });
};

export const resolveRawTextEnv = ({
    envKey,
    defaultValue,
    env,
}: {
    envKey: string;
    defaultValue: string;
    env?: EnvMap;
}): string => {
    const outcome = parseRawTextValue(getEnvRawValue(env, envKey));
    if (outcome.status !== 'value') {
        return defaultValue;
    }

    return outcome.value;
};

export const resolveTrimmedTextEnv = ({
    envKey,
    defaultValue,
    env,
}: {
    envKey: string;
    defaultValue: string;
    env?: EnvMap;
}): string => {
    const outcome = parseTrimmedTextValue(getEnvRawValue(env, envKey));
    if (outcome.status !== 'value') {
        return defaultValue;
    }

    return outcome.value;
};

export const resolveTokenizedTextEnv = ({
    envKey,
    defaultValue,
    env,
}: {
    envKey: string;
    defaultValue: string;
    env?: EnvMap;
}): string => {
    const outcome = parseTokenizedTextValue(getEnvRawValue(env, envKey));
    if (outcome.status !== 'value') {
        return defaultValue;
    }

    return outcome.value;
};

export const resolveIntegerFloorAtLeastEnv = ({
    envKey,
    defaultValue,
    minValue,
    env,
    policyMode = 'strict',
    warn,
    invalidWarning,
}: {
    envKey: string;
    defaultValue: number;
    minValue: number;
    env?: EnvMap;
    policyMode?: EnvPolicyMode;
    warn?: EnvWarnFn;
    invalidWarning?: (raw: string, defaultValue: number) => string;
}): number => {
    const raw = getEnvRawValue(env, envKey);
    const positiveNumberOutcome = parsePositiveNumberValue(raw);
    const normalizedOutcome: EnvParseOutcome<number> =
        positiveNumberOutcome.status === 'value'
            ? positiveNumberOutcome.value >= minValue
                ? { status: 'value', value: Math.floor(positiveNumberOutcome.value) }
                : invalidOutcome(raw)
            : positiveNumberOutcome;

    return resolveParsedEnvOutcome({
        envKey,
        outcome: normalizedOutcome,
        policyMode,
        onEmpty: () => defaultValue,
        onInvalid: () => defaultValue,
        warn,
        invalidWarning: invalidWarning ? (value) => invalidWarning(value, defaultValue) : undefined,
    });
};
